import * as React from 'react';
import { useState } from 'react';

const MCP_URL = 'https://bravo.brkthru.com/mcp';

export function McpSetup() {
  const [copied, setCopied] = useState(false);

  async function copyUrl() {
    await navigator.clipboard.writeText(MCP_URL);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-8">
      <div>
        <h1 className="text-2xl font-bold">Bravo MCP</h1>
        <p className="text-muted-foreground mt-2">
          Connect Claude Desktop, Claude Code, Claude.ai, or ChatGPT to Bravo via the Model Context
          Protocol. All you need is the server URL — no extension, no client ID.
        </p>
        <div className="relative mt-4">
          <pre className="bg-muted overflow-x-auto rounded-md p-4 text-sm">{MCP_URL}</pre>
          <button
            onClick={copyUrl}
            className="bg-primary text-primary-foreground hover:bg-primary/90 absolute right-2 top-2 rounded-md px-3 py-1 text-xs font-medium">
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <p className="text-muted-foreground mt-2 text-sm">
          On first connect a browser window opens for Microsoft sign-in, then tools like{' '}
          <Code>whoami</Code> become available.
        </p>
      </div>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Add to your client</h2>
        <ul className="list-inside list-disc space-y-2 text-sm">
          <li>
            <span className="font-medium">Claude Desktop</span>: Settings → Connectors → Add custom
            connector → paste the URL above.
          </li>
          <li>
            <span className="font-medium">Claude Code</span>: run{' '}
            <Code>claude mcp add --transport http bravo {MCP_URL}</Code>, then <Code>/mcp</Code> to
            authenticate.
          </li>
          <li>
            <span className="font-medium">Claude.ai</span>: Settings → Connectors → Add custom
            connector → paste the URL above.
          </li>
          <li>
            <span className="font-medium">ChatGPT</span>: Settings → Connectors → enable Developer
            mode under Advanced, then add a connector with the URL above as the MCP server URL.
          </li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Previously used the Bravo extension?</h2>
        <p className="text-sm">
          The Claude Desktop extension (<Code>.mcpb</Code> file) is retired. Remove it once, then
          add the URL instead:
        </p>
        <ol className="list-inside list-decimal space-y-1 text-sm">
          <li>In Claude Desktop, open Settings → Extensions and remove the Bravo extension.</li>
          <li>Settings → Connectors → Add custom connector → paste the URL above.</li>
          <li>Sign in with Microsoft when prompted.</li>
        </ol>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Discovery endpoints</h2>
        <ul className="list-inside list-disc space-y-1 text-sm">
          <li>
            <a
              href="/.well-known/oauth-protected-resource"
              className="text-primary hover:underline">
              /.well-known/oauth-protected-resource
            </a>
          </li>
          <li>
            <a
              href="/.well-known/oauth-authorization-server"
              className="text-primary hover:underline">
              /.well-known/oauth-authorization-server
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return <code className="bg-muted rounded px-1 py-0.5 text-xs">{children}</code>;
}
